"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "@/lib/i18n/context";

interface HeroSectionProps {
  session: { user: { name?: string | null } } | null;
}

// Two-tier emphasis parser for the hero subtitle. Editorial cover treatment
// (Variant A from the Naslovnica handoff): every marked phrase becomes italic
// Fraunces; the strongest three (**…**) get the terracotta accent, the rest
// (*…*) stay in ink. Keeps copy single-source in i18n while letting the
// renderer apply typographic weight.
type EmphasisToken = { kind: "text" | "em" | "accent"; text: string };

function parseEmphasis(s: string): EmphasisToken[] {
  const tokens: EmphasisToken[] = [];
  // Walk **accent** segments first; whatever's between them is searched for *em*.
  for (const seg of s.split(/(\*\*[^*]+\*\*)/g)) {
    if (!seg) continue;
    if (seg.startsWith("**") && seg.endsWith("**")) {
      tokens.push({ kind: "accent", text: seg.slice(2, -2) });
      continue;
    }
    for (const inner of seg.split(/(\*[^*]+\*)/g)) {
      if (!inner) continue;
      if (inner.startsWith("*") && inner.endsWith("*") && inner.length > 1) {
        tokens.push({ kind: "em", text: inner.slice(1, -1) });
      } else {
        tokens.push({ kind: "text", text: inner });
      }
    }
  }
  return tokens;
}

// Map current local hour → nearest phase index for reduced-motion fallback.
function nearestPhaseForNow(): number {
  const h = new Date().getHours() + new Date().getMinutes() / 60;
  const anchors = [6.5, 9, 18.5, 22];
  let best = 0;
  let bestDist = Infinity;
  anchors.forEach((a, i) => {
    const d = Math.abs(h - a);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}

export function HeroSection({ session }: HeroSectionProps) {
  const { t } = useTranslation();
  const c = t.landing.cover.hero;
  const ctaHref = session?.user ? "/today" : "/register";
  const ctaLabel = session?.user ? t.landing.hero.openApp : c.ctaPrimary;
  const reduced = useReducedMotion();

  const [activeIndex, setActiveIndex] = useState(() => (reduced ? nearestPhaseForNow() : 0));

  useEffect(() => {
    if (reduced) {
      setActiveIndex(nearestPhaseForNow());
      return;
    }
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % 4);
    }, 1500);
    return () => clearInterval(id);
  }, [reduced]);

  // Sticky mobile CTA: stay visible across the page, but slide away while the
  // page's own closing CTA is on screen (avoids two stacked primary actions).
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const target = document.getElementById("final-cta");
    if (!target || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setStuck(entry.isIntersecting), { threshold: 0 });
    io.observe(target);
    return () => io.disconnect();
  }, []);

  const nodePositions = [
    // Push top/bottom labels further from their node so the italic
    // Fraunces phase name doesn't kiss the active circle's pulse halo.
    { cx: 280, cy: 150, lx: 280, ly: 100, tx: 280, ty: 118, anchor: "middle" as const },
    { cx: 410, cy: 280, lx: 444, ly: 276, tx: 444, ty: 292, anchor: "start" as const },
    { cx: 280, cy: 410, lx: 280, ly: 462, tx: 280, ty: 478, anchor: "middle" as const },
    // Node 4's label ("Tomorrow's Preview") is the longest — pull it a touch
    // further left of the node for breathing room (paired with a slightly
    // smaller font below so the long phrase doesn't kiss the card's left edge).
    { cx: 150, cy: 280, lx: 108, ly: 272, tx: 108, ty: 288, anchor: "end" as const },
  ];

  const activeNodeLabel = c.nodes[activeIndex]?.label ?? "";

  return (
    <>
    <section className="relative max-w-[1320px] mx-auto px-5 md:px-12 pt-4 md:pt-[72px] pb-6 md:pb-10 z-[2] md:min-h-[calc(100svh-72px)] md:flex md:flex-col md:justify-center">
      <div className="grid md:grid-cols-[1.05fr_1fr] gap-2 md:gap-16 items-center">
        <div>
          {/* Purpose eyebrow — small, discreet uppercase chip above the H1.
             Gives Google's OAuth brand review an unmistakable, prominent
             statement of what the app is, without touching the editorial H1. */}
          <span className="mb-3 md:mb-5 inline-flex items-center rounded-full border border-[var(--rule)] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
            {c.purposeLine}
          </span>
          <h1
            className="font-display text-[clamp(36px,4.2vw,60px)] leading-[1.02] tracking-[-0.022em] mb-2 md:mb-5 text-balance [hyphens:none]"
            style={{ fontWeight: 350 }}
          >
            {c.headline.line1A}{" "}
            <em className="text-[var(--terracotta)] italic">{c.headline.line1Em}</em>
            {c.headline.line1B}{" "}
            {c.headline.line2A}{" "}
            <em className="text-[var(--terracotta)] italic">{c.headline.line2Em}</em>
            {c.headline.line2B}
          </h1>

          {/* Mobile: a short, scannable lead so the daily-rhythm figure clears
             the first fold. The full editorial description shows from md up. */}
          <p className="md:hidden text-[16px] leading-[1.6] text-[var(--ink-soft)] mb-3 text-pretty">
            {c.descriptionShort}
          </p>
          <p className="hidden md:block text-[18px] leading-[1.6] text-[var(--ink-soft)] max-w-[560px] mb-7 text-pretty">
            {parseEmphasis(c.description).map((p, i) => {
              if (p.kind === "accent") {
                return (
                  <em
                    key={i}
                    className="font-display italic text-[1.02em] text-[var(--terracotta)]"
                    style={{ fontVariationSettings: '"opsz" 72, "SOFT" 90' }}
                  >
                    {p.text}
                  </em>
                );
              }
              // Non-accent emphases render as plain body text — only the
              // terracotta accents get the editorial italic treatment.
              return <span key={i}>{p.text}</span>;
            })}
          </p>

          <div className="hidden md:flex flex-wrap items-center gap-3 md:mb-5">
            {/* Primary CTA — hidden on mobile (it lives in the sticky bar). */}
            <Link
              href={ctaHref}
              className="hidden md:inline-flex items-center gap-2 px-[26px] py-4 rounded-xl bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium text-[15px] transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
            >
              {ctaLabel}
            </Link>
          </div>

          {/* Trust line — hidden on mobile (shown in the sticky bar). */}
          <p className="hidden md:block font-mono text-xs text-[var(--ink-soft)] tracking-[0.04em]">
            {c.trust.join(" ")}
          </p>
        </div>

        <div className="w-full md:max-w-[560px] md:ml-auto md:aspect-square relative">
          {/* Mobile: rectangular daily-rhythm timeline. The square loop is cut
             off on a phone fold, so this compact landscape strip fits and
             animates the flow start → end. */}
          <div
            className="md:hidden w-full rounded-[18px] border border-[var(--hair)] overflow-hidden px-5 pt-3 pb-3"
            style={{
              background:
                "radial-gradient(120% 90% at 50% 0%, rgba(184,83,26,0.06), transparent 60%), linear-gradient(180deg, var(--paper), var(--paper-2))",
              boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 18px 44px rgba(26,24,21,0.07)",
            }}
          >
            <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--ink-soft)] flex gap-2.5">
              <span>{c.fig}</span>
              <span className="opacity-40">·</span>
              <span>{c.figLabel}</span>
            </div>

            <div className="relative mt-4">
              {/* Base connector + animated progress fill (aligned to circle centres) */}
              <div className="absolute left-[12.5%] right-[12.5%] top-[15px] h-[2px] -translate-y-1/2 rounded-full bg-[var(--rule)]" />
              <div
                className="absolute left-[12.5%] top-[15px] h-[2px] -translate-y-1/2 rounded-full transition-[width] duration-700 ease-out"
                style={{
                  width: `${(activeIndex / 3) * 75}%`,
                  background: "linear-gradient(90deg, var(--terracotta), var(--moss))",
                }}
              />
              <div className="relative grid grid-cols-4">
                {c.nodes.map((n, i) => {
                  const isActive = i === activeIndex;
                  const isPast = i < activeIndex;
                  const isOptional = n.optional === true;
                  return (
                    <div key={n.code} className="flex flex-col items-center px-1 text-center">
                      <div className="relative grid h-[30px] w-[30px] place-items-center">
                        {isActive && !reduced && (
                          <span className="absolute inset-0 rounded-full bg-[var(--terracotta)]/25 animate-ping" />
                        )}
                        <div
                          className="grid h-[30px] w-[30px] place-items-center rounded-full font-mono text-[11px] font-semibold transition-colors duration-500"
                          style={{
                            borderWidth: isOptional ? "1px" : "0.9px",
                            borderStyle: isOptional ? "dashed" : "solid",
                            borderColor: isOptional && !isActive ? "var(--terracotta)" : "var(--ink)",
                            background: isActive ? "var(--terracotta)" : isPast ? "var(--paper-2)" : "var(--paper)",
                            color: isActive ? "var(--paper)" : "var(--terracotta)",
                          }}
                        >
                          {i + 1}
                        </div>
                      </div>
                      <div className="mt-2 font-mono text-[10px] tracking-[0.08em] text-[var(--ink-soft)]">
                        {n.code}
                      </div>
                      <div className="mt-0.5 font-display italic text-[11px] leading-[1.15] text-[var(--ink)] [text-wrap:balance]">
                        {n.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 font-display italic text-[15px] leading-[1.4] text-[var(--ink-soft)] text-center text-balance">
              {c.figSub}
            </div>
          </div>

          {/* Desktop / tablet: the square alignment loop */}
          <div
            className="hidden md:block absolute inset-0 rounded-[18px] md:rounded-[24px] border border-[var(--hair)] overflow-hidden"
            style={{
              background:
                "radial-gradient(120% 80% at 50% 0%, rgba(184,83,26,0.06), transparent 60%), linear-gradient(180deg, var(--paper), var(--paper-2))",
              boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
            }}
          >
            <div className="absolute top-[18px] left-5 font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--ink-soft)] flex gap-3.5">
              <span>{c.fig}</span>
              <span className="opacity-40">·</span>
              <span>{c.figLabel}</span>
            </div>
            <div className="absolute bottom-[22px] left-[22px] font-display italic text-[18px] text-[var(--ink-soft)]">
              {c.figSub}
            </div>

            {/* NOW caption — crossfades with each active node tick */}
            <div className="absolute top-[44px] left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)] pointer-events-none">
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeIndex}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block"
                >
                  NOW · {activeNodeLabel}
                </motion.span>
              </AnimatePresence>
            </div>

            <svg className="absolute inset-0 w-full h-full" viewBox="-32 -32 624 624" aria-hidden="true">
              <circle cx="280" cy="280" r="130" fill="none" stroke="var(--ink)" strokeWidth="0.6" opacity="0.35" strokeDasharray="2 4" />
              <path d="M 280 150 A 130 130 0 0 1 410 280" fill="none" stroke="var(--terracotta)" strokeWidth="1.4" strokeLinecap="round" />
              <path d="M 410 280 A 130 130 0 0 1 280 410" fill="none" stroke="var(--moss)" strokeWidth="1.4" strokeLinecap="round" />
              <path d="M 280 410 A 130 130 0 0 1 150 280" fill="none" stroke="var(--moss)" strokeWidth="1.4" strokeLinecap="round" />
              <path d="M 150 280 A 130 130 0 0 1 280 150" fill="none" stroke="var(--terracotta)" strokeWidth="1.4" strokeLinecap="round" />

              {c.nodes.map((n, i) => {
                const p = nodePositions[i];
                const isOptional = n.optional === true;
                const isActive = i === activeIndex;
                const dimOpacity = isOptional ? 0.7 : 1;
                return (
                  <g key={n.code}>
                    {/* Pulse halo for active node */}
                    {isActive && !reduced && (
                      <motion.circle
                        cx={p.cx}
                        cy={p.cy}
                        r={18}
                        fill="var(--terracotta)"
                        initial={{ opacity: 0.4, scale: 1 }}
                        animate={{ opacity: 0, scale: 1.6 }}
                        transition={{ duration: 1.4, ease: "easeOut", repeat: Infinity }}
                        style={{ transformOrigin: `${p.cx}px ${p.cy}px` }}
                      />
                    )}
                    <motion.circle
                      cx={p.cx}
                      cy={p.cy}
                      r={18}
                      fill="var(--paper)"
                      stroke="var(--ink)"
                      strokeWidth={0.8}
                      strokeDasharray={isOptional ? "3 3" : undefined}
                      strokeOpacity={isOptional ? 0.6 : 1}
                      animate={{
                        scale: isActive ? 1.15 : 1,
                      }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      style={{ transformOrigin: `${p.cx}px ${p.cy}px` }}
                    />
                    <motion.circle
                      cx={p.cx}
                      cy={p.cy}
                      r={11}
                      stroke="var(--terracotta)"
                      strokeWidth={isOptional ? 0.8 : 0.6}
                      strokeDasharray={isOptional ? "2 2" : undefined}
                      strokeOpacity={isOptional ? 0.55 : 1}
                      animate={{
                        fill: isActive ? "var(--terracotta)" : isOptional ? "var(--paper)" : "var(--paper-2)",
                        scale: isActive ? 1.15 : 1,
                      }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      style={{ transformOrigin: `${p.cx}px ${p.cy}px` }}
                    />
                    <text
                      x={p.cx}
                      y={p.cy + 4}
                      textAnchor="middle"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        fill: isActive ? "var(--paper)" : "var(--terracotta)",
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        opacity: isOptional ? 0.55 : 1,
                        transition: "fill 0.4s",
                      }}
                    >
                      {i + 1}
                    </text>
                    <text
                      x={p.lx}
                      y={p.ly}
                      textAnchor={p.anchor}
                      style={{ fontFamily: "var(--font-mono)", fontSize: 9, fill: "var(--ink-soft)", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 500, opacity: dimOpacity }}
                    >
                      {n.code}
                    </text>
                    <text
                      x={p.tx}
                      y={p.ty}
                      textAnchor={p.anchor}
                      style={{ fontFamily: "var(--font-display)", fontSize: i === 3 ? 12 : 13, fill: "var(--ink)", fontStyle: "italic", fontWeight: 400, opacity: dimOpacity }}
                    >
                      {n.label}
                    </text>
                    {isOptional && n.optionalLabel && (
                      <text
                        x={p.tx}
                        y={p.ty + 14}
                        textAnchor={p.anchor}
                        style={{ fontFamily: "var(--font-display)", fontSize: 11, fill: "var(--ink)", fontStyle: "italic", letterSpacing: "0.04em", opacity: 0.45 }}
                      >
                        {n.optionalLabel}
                      </text>
                    )}
                  </g>
                );
              })}

              <g style={{ transformOrigin: "280px 280px", animation: "jn-spin-slow 60s linear infinite" }}>
                <circle cx="280" cy="280" r="40" fill="var(--paper)" stroke="var(--ink)" strokeWidth="0.6" strokeOpacity="0.28" />
                <circle cx="280" cy="280" r="30" fill="none" stroke="var(--ink)" strokeWidth="0.4" strokeOpacity="0.18" strokeDasharray="1 3" />
                <path d="M280 243 L285 280 L280 286 L275 280 Z" fill="var(--terracotta)" />
              </g>
              <text x="280" y="340" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: 11, fill: "var(--ink-soft)", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>
                {c.goalsLabel}
              </text>
              <text x="280" y="356" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: 10, fill: "var(--ink-soft)", opacity: 0.55, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                {c.goalsSub}
              </text>
            </svg>
          </div>
        </div>
      </div>
    </section>

    {/* Sticky mobile CTA bar — primary action always within thumb reach.
        Hidden ≥ md; slides away while the page's closing CTA is on screen. */}
    <div
      data-mobile-cta
      className={`md:hidden fixed inset-x-0 bottom-0 z-50 border-t border-[var(--rule)] px-5 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] transition-transform duration-200 ease-out motion-reduce:transition-none ${stuck ? "translate-y-[140%]" : "translate-y-0"}`}
      style={{
        background: "color-mix(in oklab, var(--paper) 88%, transparent)",
        backdropFilter: "blur(10px) saturate(1.8)",
        WebkitBackdropFilter: "blur(10px) saturate(1.8)",
      }}
    >
      <Link
        href={ctaHref}
        className="block w-full rounded-xl bg-[var(--terracotta)] py-[14px] text-center text-[15px] font-medium text-[var(--terracotta-soft)]"
      >
        {ctaLabel}
      </Link>
      <p className="mt-2 text-center font-mono text-[10px] leading-[1.35] tracking-[0.04em] text-[var(--ink-soft)] text-balance">
        {c.trust.join(" ")}
      </p>
    </div>
    </>
  );
}
